import Cookie from 'js-cookie';
import moment from 'moment';
import * as api from '../api';
import * as utils from '../utils';
import { G2S } from '../g2s';

export class AuthService {
    private accountService = new api.AccountService();

    public currentUser: api.UserWithBonuses | null = null;

    public authenticated = false;

    constructor() {
        this.accountService = new api.AccountService();

        this.authenticated = AuthService.isAuthCookie();
    }
    
    public confirm(token: string): Promise<api.User> {
        return new Promise<api.User>((resolve, reject) => {
            this.accountService.confirm(token)
                .then((response: api.User) => {
                    new utils.GoogleAnalyticsService().sendPageview('/registration/confirmation', 'Registration / Confirmation');

                    this.signInClientSide();

                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getRefundInfo(locale: string, reservationPublicId?: string): Promise<api.RefundInfo> {
        return this.accountService.getRefundInfo(locale, reservationPublicId);
    }

    public getRefundInfoWithWriteUs(locale: string, reservationId?: string): Promise<api.RefundInfo> {
        return this.accountService.getRefundInfoWithWriteUs(locale, reservationId);
    }

    public getSupportText(locale: string): Promise<api.AutovoidSupportText> {
        return this.accountService.getSupportText(locale);
    }

    public getProcessStartedText(locale: string): Promise<api.AutovoidStartedText>{
        return this.accountService.getProcessStartedText(locale);
    }

    public checkVoidAvailability(reservationId: string): Promise<api.CheckAutoVoidAvailable> {
        return this.accountService.checkVoidAvailability(reservationId);
    }

    public ordersExists(reservationId: string) {
        return this.accountService.ordersExists(reservationId);
    }

    public createVoid(reservationPublicId: string): Promise<boolean> {
        return this.accountService.createVoid(reservationPublicId);
    }

    public createVoidSupport(reservationId: string, email: string, description: string | null): Promise<boolean> {
        return this.accountService.createVoidSupport(reservationId, email, description);
    }

    public confirmEmail(token: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.accountService.confirmEmail(token)
                .then((response: boolean) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public deleteStart(): Promise<boolean> {
        return this.accountService.deleteStart();
    }

    public deleteConfirm(token: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.accountService.deleteConfirm(token)
                .then((response: boolean) => {
                    new utils.GoogleAnalyticsService().sendPageview('/account/delete/confirm', 'Account / Delete / Confirm');

                    this.signOutClientSide();

                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public exists(emailOrPhone: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.accountService.exists(emailOrPhone)
                .then((response: boolean) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public forgottenPassword(email: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.accountService.forgottenPassword(email)
                .then((response: boolean) => {
                    new utils.GoogleAnalyticsService().sendPageview('/password/forgotten/submitted', 'Password / Forgotten / Submitted');

                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getBonusStatus(): Promise<api.BonusStatus> {
        return this.accountService.getBonusStatus();
    }

    public getCurrentUser(): Promise<api.User> {
        return new Promise<api.User>((resolve, reject) => {
            if (this.currentUser) {
                if (this.currentUser.resetMarker) {
                    utils.Cookies.removePartnerCookie();
                }
                // use cached
                resolve(this.currentUser);
            } else {
                this.accountService.getCurrentUserWithBonuses()
                    .then((response: api.UserWithBonuses) => {
                        if (response.resetMarker) {
                            utils.Cookies.removePartnerCookie();
                        }
                        this.currentUser = response;
                        resolve(response);
                    })
                    .catch((error) => {
                        if (error.response && (error.response.status == 401 || error.response.status == 403)) {
                            this.signOutClientSide();
                        }
                        reject(error);
                    });
            }
        });
    }

    public getCurrentUserAccount(): Promise<api.UserAccount> {
        return new Promise<api.UserAccount>((resolve, reject) => {
            this.accountService.getCurrentUserAccount()
                .then((response: api.UserAccount) => {
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response && (error.response.status == 401 || error.response.status == 403)) {
                        this.signOutClientSide();
                    }

                    reject(error);
                });
        });
    }

    public getCurrentUserWithBonuses(bookingSessionId?: number): Promise<api.UserWithBonuses> {
        return new Promise<api.UserWithBonuses>((resolve, reject) => {
            this.accountService.getCurrentUserWithBonuses(bookingSessionId)
                .then((response: api.UserWithBonuses) => {
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response && (error.response.status == 401 || error.response.status == 403)) {
                        this.signOutClientSide();
                    }

                    reject(error);
                });
        });
    }

    public getCurrentUserWithProfile(): Promise<api.UserWithProfile> {
        return this.accountService.getCurrentUserWithProfile();
    }

    public ordersList(): Promise<api.ReservationStatusResult[]> {
        return this.accountService.ordersList();
    }

    public GetActiveReservationPublicId(): Promise<api.ActiveReservationPublicId>{
        return this.accountService.GetActiveReservationPublicId();
    }

    public registration(email: string, password: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.accountService.registration(email, password)
                .then((response: boolean) => {
                    new utils.GoogleAnalyticsService().sendPageview('/registration/submitted', 'Registration / Submitted');

                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public setPassword(token: string, password: string): Promise<boolean> {
        return this.accountService.setPassword(token, password);
    }

    public signIn(emailOrPhone: string, password: string): Promise<api.User> {
        return new Promise<api.User>((resolve, reject) => {
            this.accountService.signIn(emailOrPhone, password)
                .then((response: api.User) => {
                    const retailRocket = new api.RetailRocket();
                    retailRocket.userAuthenticated({}).finally(() => {
                        this.signInClientSide();

                        new utils.GoogleAnalyticsService().sendPageview('/signin/success', 'Signin / Success');

                        resolve(response);
                    });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public signInClientSide() {
        // It's ok. Let's set the cookie to prevent unessesary requests.
        Cookie.set('g2sATKN', true, {      
            path: '/',
            expires: moment().add(1, 'year').toDate(),
            domain: G2S.config.cookieDomain,
            sameSite: G2S.isSecure ? 'none' : undefined,
            secure: G2S.isSecure
        });

        this.authenticated = true;
    }

    public signOut(): Promise<boolean> {
        this.signOutClientSide();

        new utils.GoogleAnalyticsService().sendPageview('/signout', 'Signout');

        // Sign out server-side
        return new Promise<boolean>((resolve, reject) => {
            this.accountService.signOut()
                .then((response: boolean) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public sendOneTimePassword(email: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.accountService.sendOneTimePassword(email)
                .then((response: boolean) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public signInByOneTimePassword(email: string, oneTimePassword: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.accountService.signInByOneTimePassword(email, oneTimePassword)
                .then((response: boolean) => {
                    this.signInClientSide();
                    this.signInClientSide();

                    new utils.GoogleAnalyticsService().sendPageview('/signin/success', 'Signin / Success');
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public signOutClientSide() {
        Cookie.remove('g2sATKN', {
            path: '/',
            domain: G2S.config.cookieDomain,
            sameSite: G2S.isSecure ? 'none' : undefined,
            secure: G2S.isSecure
        });

        this.authenticated = false;
        this.currentUser = null;
    }

    public updateEmail(email: string, currentPassword: string): Promise<boolean> {
        return this.accountService.updateEmail(email, currentPassword);
    }

    public updatePassword(newPassword: string, currentPassword: string): Promise<boolean> {
        return this.accountService.updatePassword(newPassword, currentPassword);
    }

    public updatePhone(phone: string, currentPassword: string): Promise<boolean> {
        return this.accountService.updatePhone(phone.replace(/[^\d]/gi, ''), currentPassword);
    }

    public updateProfile(user: api.User): Promise<boolean> {
        return this.accountService.updateProfile(user);
    }

    private static isAuthCookie(): boolean {
        if (Cookie.get('g2sATKN')) {
            return true;
        } else {
            return false;
        }
    }

    // Static method to use in vue router
    public static routeAnonymousOnly(to, from, next) {
        if (AuthService.isAuthCookie()) {
            next({
                path: '/account'
            });
        } else {
            next();
        }
    }

    // Static method to use in vue router
    public static routeAuthenticatedOnly(to, from, next) {
        if (!AuthService.isAuthCookie()) {
            if (G2S.config.clientDevice.type === 'desktop') {
                next({
                    path: '/'
                });
            } else {
                next({
                    path: '/signin',
                    query: { redirect: to.fullPath }
                });
            }
        } else {
            next();
        }
    }

}
